import { Link } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useActiveTrip, type ActiveTrip } from '../hooks/useActiveTrip';
import { useDailyReports, latestReport, reportStatus, type DailyReport } from '../hooks/useDailyReports';
import { TripPicker } from '../components/TripPicker';
import { LoadingBlock, NoActiveTrip } from '../components/PageState';
import { FactList } from '../components/FactList';
import { EM_DASH, formatDate, formatSolutionType, humanize, pluralize } from '../lib/formatters';
import { tripDayInfo } from '../lib/tripDates';
import { hrefWithTrip } from '../lib/tripQuery';
import { errorMessage } from '../lib/errors';

export function DashboardPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const reportsQuery = useDailyReports(trip?.id);

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  const reports = reportsQuery.data ?? [];
  const latest = latestReport(reports);
  const day = tripDayInfo(trip.start_date, trip.end_date);
  const link = (href: string) => hrefWithTrip(href, trip.id);
  const solutionTypes = trip.solution_types ?? [];

  const reportsValue = reportsQuery.isLoading
    ? '…'
    : reportsQuery.isError
      ? 'Unavailable'
      : String(reports.length);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-text">Project overview</h1>
          <p className="mt-0.5 truncate text-sm text-text-muted">
            <span className="font-mono">{trip.project_number}</span>
            {trip.location?.name ? ` · ${trip.location.name}` : ''}
          </p>
        </div>
        <StatusBadge status={trip.status ?? 'scheduled'} />
      </div>

      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Trip day" value={day.label} sub={day.totalDays ? `${pluralize(day.totalDays, 'calendar day')} planned` : 'Set start and end dates in admin'} />
        <Stat label="Reports filed" value={reportsValue} sub={reportsQuery.isError ? errorMessage(reportsQuery.error) : reports.length === 0 && !reportsQuery.isLoading ? 'None yet' : undefined} />
        <Stat label="Last report" value={latest ? formatDate(latest.report_date) : EM_DASH} sub={latest ? humanize(reportStatus(latest)) : 'No report filed'} />
        <Stat label="Status" value={humanize(trip.status ?? 'scheduled')} sub={trip.lead_technician?.full_name ? `Lead: ${trip.lead_technician.full_name}` : 'No lead technician assigned'} />
      </div>

      <section className="card p-4" aria-labelledby="next-work-heading">
        <h2 id="next-work-heading" className="card-title">
          Next planned work
        </h2>
        {latest?.tomorrow_plan ? (
          <>
            <p className="mt-2 whitespace-pre-wrap text-sm text-text">{latest.tomorrow_plan}</p>
            <p className="mt-2 text-xs text-text-faint">From the report for {formatDate(latest.report_date)}</p>
          </>
        ) : (
          <p className="mt-2 text-sm text-text-muted">
            No plan recorded yet. Add tomorrow&apos;s plan in{' '}
            <Link to={link('/daily-report')} className="text-primary underline">
              today&apos;s daily report
            </Link>
            .
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="card p-4" aria-labelledby="project-details-heading">
          <h2 id="project-details-heading" className="card-title mb-3">
            Project details
          </h2>
          <ProjectFacts trip={trip} />
          {solutionTypes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {solutionTypes.map((t) => (
                <span key={t} className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
                  {formatSolutionType(t)}
                </span>
              ))}
            </div>
          )}
        </section>

        <section className="card p-4" aria-labelledby="site-tools-heading">
          <h2 id="site-tools-heading" className="card-title mb-2">
            Site tools
          </h2>
          <ul className="divide-y divide-border">
            <ToolLink to={link('/daily-report')} label="Daily report" detail={reportsQuery.isLoading ? 'Loading…' : reportsQuery.isError ? 'Unavailable' : reports.length > 0 ? `${pluralize(reports.length, 'report')} filed` : 'None filed yet'} />
            <ToolLink to={link('/network')} label="Network documentation" detail="Topology, VLANs, wireless" />
            <ToolLink to={link('/inventory')} label="Hardware inventory" detail="Assets at this site" />
            <ToolLink to={link('/lifecycle')} label="Hardware lifecycle" detail="Warranty and firmware" />
            <ToolLink to={link('/intel')} label="Site intel" detail="Confidential observations" />
          </ul>
        </section>
      </div>

      <section className="card p-4" aria-labelledby="reports-heading">
        <h2 id="reports-heading" className="card-title mb-3">
          Daily reports
        </h2>
        {reportsQuery.isLoading ? (
          <LoadingBlock label="Loading reports…" />
        ) : reportsQuery.isError ? (
          <p role="alert" className="text-sm text-error">
            Could not load reports: {errorMessage(reportsQuery.error)}
          </p>
        ) : reports.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title="No reports filed"
            subtitle="File today's report from the Daily report screen. Each filed day appears here."
          />
        ) : (
          <ul className="space-y-3">
            {[...reports].reverse().map((r) => (
              <DayCard key={r.id} report={r} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</p>
      <p className="mt-1 break-words text-lg font-bold leading-tight text-text">{value}</p>
      {sub && <p className="mt-0.5 break-words text-xs text-text-muted">{sub}</p>}
    </div>
  );
}

function ToolLink({ to, label, detail }: { to: string; label: string; detail: string }) {
  return (
    <li>
      <Link
        to={to}
        className="flex min-h-[48px] items-center justify-between gap-3 rounded-md px-1 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span className="text-sm font-medium text-text">{label}</span>
        <span className="shrink-0 text-right text-xs text-text-muted">{detail}</span>
      </Link>
    </li>
  );
}

function ProjectFacts({ trip }: { trip: ActiveTrip }) {
  const phone = trip.location?.contact_phone;
  return (
    <FactList
      facts={[
        { label: 'Project #', value: trip.project_number, mono: true },
        { label: 'Customer', value: trip.customer?.name ?? null },
        { label: 'Location', value: trip.location?.name ?? null },
        { label: 'Site contact', value: trip.location?.contact_name ?? null },
        {
          label: 'Contact phone',
          value: phone ? (
            <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="text-primary underline">
              {phone}
            </a>
          ) : null,
        },
        { label: 'Lead tech', value: trip.lead_technician?.full_name ?? trip.lead_technician?.email ?? null },
        { label: 'Dates', value: trip.start_date || trip.end_date ? `${formatDate(trip.start_date)} – ${formatDate(trip.end_date)}` : null },
        { label: 'PO #', value: trip.po_number ?? null, mono: true },
        { label: 'Scope', value: trip.scope ? <span className="whitespace-pre-wrap font-normal">{trip.scope}</span> : null },
      ]}
    />
  );
}

function DayCard({ report }: { report: DailyReport }) {
  const status = reportStatus(report);
  return (
    <li>
      <details className="group card px-3 py-2">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-text-inverse" aria-hidden="true">
              D{report.day_number}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-text">
                Day {report.day_number} · {formatDate(report.report_date)}
              </p>
              <StatusBadge status={status} />
            </div>
          </div>
          <span className="text-text-faint transition-transform group-open:rotate-90" aria-hidden="true">
            &rsaquo;
          </span>
        </summary>
        <div className="mt-2 space-y-3 pb-1 pl-11">
          {(report.hours_on_site || report.crew_on_site) && (
            <p className="text-xs text-text-muted">
              {[report.hours_on_site, report.crew_on_site].filter(Boolean).join(' · ')}
            </p>
          )}
          {report.work_completed && (
            <div>
              <p className="text-xs font-semibold uppercase text-text-muted">Work completed</p>
              <p className="whitespace-pre-wrap text-sm text-text">{report.work_completed}</p>
            </div>
          )}
          {report.tomorrow_plan && (
            <div>
              <p className="text-xs font-semibold uppercase text-text-muted">Tomorrow&apos;s plan</p>
              <p className="whitespace-pre-wrap text-sm text-text">{report.tomorrow_plan}</p>
            </div>
          )}
          {!report.work_completed && !report.tomorrow_plan && (
            <p className="text-sm text-text-muted">No customer-facing notes on this report.</p>
          )}
          {(report.internal_notes || report.punch_list) && (
            <div className="rounded-r-md border-l-2 border-accent-purple bg-accent-purple-soft p-3">
              <p className="mb-1 text-[11px] font-bold uppercase text-accent-purple">Internal — never shared with the customer</p>
              {report.internal_notes && <p className="whitespace-pre-wrap text-xs text-text">{report.internal_notes}</p>}
              {report.punch_list && (
                <div className="mt-1">
                  <p className="text-[11px] font-semibold uppercase text-text-muted">Punch list</p>
                  <p className="whitespace-pre-wrap text-xs text-text">{report.punch_list}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </details>
    </li>
  );
}
